// FreeCell (owner 2026-10-23: the next most played solitaire, the third of the Windows classics after Klondike
// and Spider). One deck, all 52 cards dealt face up into 8 columns (7, 7, 7, 7, 6, 6, 6, 6), no stock; 4 free
// cells hold one card each; 4 foundations build up by suit from the Ace. A column builds down in alternating
// colours; any card (or a run as big as the room allows) goes to an empty column. The size of a run moved at
// once is (empty cells + 1) x 2^(empty columns, the target not counted), the moves a player could make one by
// one through the free room. Foundation cards stay there (as in the Windows game).
import type { Card, CardKey, GameState, PileRef } from '../types';
import { isRed, rankLow } from '../cards';
import { card, dropOnFoundation, pileCards, standardFoundationFor, topOf, type VariantRules } from './base';

/** Down by 1 in alternating colours. */
function buildsOn(c: Card, onto: Card): boolean {
  return isRed(c.suit) !== isRed(onto.suit) && rankLow(onto.rank) === rankLow(c.rank) + 1;
}

/** From `index` to the top, each card builds on the one under it. */
function isRun(s: GameState, col: readonly CardKey[], index: number): boolean {
  if (index < 0 || index >= col.length) return false;
  for (let i = index + 1; i < col.length; i++) if (!buildsOn(card(s, col[i]!), card(s, col[i - 1]!))) return false;
  return true;
}

/** The biggest run that may move to `target` now. */
export function maxRun(s: GameState, target: PileRef): number {
  const freeCells = s.piles.cells.filter((c) => c.length === 0).length;
  const emptyCols = s.piles.tableau.filter((col, i) => col.length === 0 && !(target.kind === 'tableau' && target.index === i)).length;
  return (freeCells + 1) * 2 ** emptyCols;
}

function canLift(s: GameState, pile: PileRef, index: number): boolean {
  if (pile.kind === 'tableau') return isRun(s, pileCards(s, pile), index);
  if (pile.kind === 'cell') return index === 0 && pileCards(s, pile).length === 1;
  return false;
}

function canDrop(s: GameState, run: readonly CardKey[], target: PileRef): boolean {
  const bottom = run[0];
  if (bottom === undefined) return false;
  if (target.kind === 'foundation') return dropOnFoundation(s, run, target.index);
  if (target.kind === 'cell') return run.length === 1 && pileCards(s, target).length === 0;
  if (target.kind !== 'tableau') return false;
  if (run.length > maxRun(s, target)) return false;
  const top = topOf(pileCards(s, target));
  if (top === undefined) return true;
  return buildsOn(card(s, bottom), card(s, top));
}

/** The rest goes home by itself (auto-move "when won"): every column runs down from bottom to top, so the
 *  lowest card left is always on top of its column or in a cell, and it is the next one its foundation needs. */
function autoFinishReady(s: GameState): boolean {
  return s.piles.tableau.every((col) => col.every((k, i) => i === 0 || rankLow(card(s, k).rank) < rankLow(card(s, col[i - 1]!).rank)));
}

export const freecell: VariantRules = {
  id: 'freecell',
  columns: [7, 7, 7, 7, 6, 6, 6, 6],
  foundations: 4,
  cells: 4,
  decks: 1,
  suits: ['c', 's', 'd', 'h'],
  variableSuits: 0,
  turn: 1,
  hasWaste: false,
  recycle: false,
  allFaceUp: true,
  autoMove: true,
  foundationToTableau: false,
  canLift,
  canDrop,
  foundationFor: standardFoundationFor,
  buildsOn,
  completedRun: () => false,
  autoFinishReady,
};
